import { useMemo, type FC } from 'react';
import { Link, useParams } from '@tanstack/react-router';
import {
  Building2,
  Calendar,
  MapPin,
  Receipt,
  Truck,
  Warehouse,
} from 'lucide-react';
import type { CustomerOrderLineDto } from '@/api';
import {
  Badge,
  Card,
  CardBody,
  CardHeader,
  CardTitle,
  Empty,
  Skeleton,
  Table,
  type TableColumn,
} from '@/ui';

import { PaymentList } from '../../components/PaymentList';
import { PaymentScheduleList } from '../../components/PaymentScheduleList';
import { resolveApiErrorMessage } from '../../lib/apiError';
import {
  formatDate,
  formatInstantDate,
  formatMoney,
  formatNumber,
  toNumber,
} from '../../lib/format';
import {
  CUSTOMER_ORDER_STATES,
  CUSTOMER_ORDER_STATUSES,
} from '../../lib/shipmentStatus';
import { useCustomerOrder } from './api/queries';
import { STATUS_TONES } from './lib/status';

const ROUTE_ID = '/_app/orders_/$orderId' as const;

/*
 * Карточка заказа 1С - шапка и строки в порядке документа, только чтение.
 * Цена и сумма строки - с НДС или без, как в заказе (priceIncludesVat);
 * сумма документа - всегда с НДС. Состояние (отгружен ли, оплачен ли) - из
 * регистра УТ; у проведённого заказа есть отгрузка в Culture (ADR-012) - ссылка
 * на неё в шапке.
 */
export const CustomerOrderCardPage: FC = () => {
  const { orderId } = useParams({ from: ROUTE_ID });
  const { data: order, isPending, isError, error } = useCustomerOrder(orderId);

  const columns = useMemo<ReadonlyArray<TableColumn<CustomerOrderLineDto>>>(
    () => [
      {
        id: 'lineNumber',
        header: '№',
        width: 'w-12',
        cell: (line) => (
          <span className="text-ink-500 tabular-nums">{line.lineNumber}</span>
        ),
      },
      {
        id: 'product',
        header: 'Товар',
        cell: (line) => (
          <div className={line.isCancelled ? 'text-ink-400' : 'text-ink-900'}>
            {line.productName ?? (
              <span className="text-ink-400">не загружен из 1С</span>
            )}
            {line.isCancelled && (
              <Badge tone="cancelled" className="ml-2">
                Отменено
              </Badge>
            )}
          </div>
        ),
      },
      {
        id: 'quantity',
        header: 'Кол-во',
        width: 'w-24',
        align: 'right',
        cell: (line) => formatNumber(toNumber(line.quantity)),
      },
      {
        id: 'price',
        header: 'Цена',
        width: 'w-32',
        align: 'right',
        cell: (line) => formatMoney(toNumber(line.price)),
      },
      {
        id: 'amount',
        header: 'Сумма',
        width: 'w-36',
        align: 'right',
        cell: (line) => formatMoney(toNumber(line.amount)),
      },
      {
        id: 'amountWithVat',
        header: 'Сумма с НДС',
        width: 'w-36',
        align: 'right',
        hideBelow: 'md',
        cell: (line) => formatMoney(toNumber(line.amountWithVat)),
      },
    ],
    [],
  );

  if (isPending)
    return (
      <div className="flex flex-col gap-3">
        <Skeleton className="h-5 w-40" />
        <Skeleton className="h-8 w-96" />
        <Skeleton className="h-40" />
      </div>
    );

  if (isError)
    return (
      <Empty>
        Заказ не найден или не загрузился: {resolveApiErrorMessage(error)}.{' '}
        <Link to="/orders" className="text-brand-green-700 hover:underline">
          К заказам 1С
        </Link>
      </Empty>
    );

  return (
    <>
      <div className="mb-5">
        <div className="mb-1.5 flex flex-wrap items-center gap-2.5">
          <span className="text-[13px] text-ink-500 tabular-nums">
            № {order.number} от {formatInstantDate(order.date)}
          </span>
          {order.status && (
            <Badge tone={STATUS_TONES[order.status]} withDot>
              {CUSTOMER_ORDER_STATUSES[order.status]}
            </Badge>
          )}
          {order.state && (
            <Badge tone="neutral">{CUSTOMER_ORDER_STATES[order.state]}</Badge>
          )}
          {!order.isActive ? (
            <Badge tone="neutral">Удалён в 1С</Badge>
          ) : (
            !order.isPosted && <Badge tone="neutral">Не проведён</Badge>
          )}
        </div>
        <h1 className="m-0 text-2xl font-semibold tracking-[-0.01em] text-ink-900">
          {order.partnerName ?? (
            <span className="text-ink-400">Клиент не загружен из 1С</span>
          )}
        </h1>
        <div className="mt-2 flex flex-wrap gap-x-[18px] gap-y-1 text-[13px] text-ink-600">
          <span className="inline-flex items-center gap-1">
            <Calendar aria-hidden className="size-[13px]" /> Отгрузка:{' '}
            <b className="text-ink-900">
              {order.shipmentDate
                ? formatDate(order.shipmentDate)
                : 'не указана'}
            </b>
          </span>
          <span className="inline-flex items-center gap-1">
            <Building2 aria-hidden className="size-[13px]" />
            {order.counterpartyName ?? (
              <span className="text-ink-400">контрагент не указан</span>
            )}
          </span>
          <span className="inline-flex items-center gap-1">
            <Warehouse aria-hidden className="size-[13px]" />
            {order.warehouseName ?? (
              <span className="text-ink-400">склад не указан</span>
            )}
          </span>
          {order.deliveryAddress && (
            <span className="inline-flex items-center gap-1">
              <MapPin aria-hidden className="size-[13px]" />
              {order.deliveryAddress}
            </span>
          )}
          <span className="inline-flex items-center gap-1">
            <Receipt aria-hidden className="size-[13px]" />
            {order.priceIncludesVat ? 'Цены с НДС' : 'Цены без НДС'}
          </span>
          {order.shipmentId && (
            <span className="inline-flex items-center gap-1">
              <Truck aria-hidden className="size-[13px]" />
              <Link
                to="/shipments/$shipmentId"
                params={{ shipmentId: order.shipmentId }}
                className="text-brand-green-700 hover:underline"
              >
                Отгрузка № {order.shipmentNumber}
              </Link>
            </span>
          )}
        </div>
        {order.comment && (
          <div className="mt-2 text-[13px] whitespace-pre-line text-ink-600">
            {order.comment}
          </div>
        )}
      </div>

      <Table
        columns={columns}
        rows={order.lines}
        getRowKey={(line) => line.lineNumber}
        emptyLabel="В заказе нет строк"
        footer={
          <div className="flex items-center justify-end gap-2.5 px-4 py-2.5 text-[12.5px] text-ink-600">
            Итого с НДС
            <b className="text-sm text-ink-900 tabular-nums">
              {formatMoney(toNumber(order.amount))}
            </b>
          </div>
        }
      />

      {order.paymentStages.length > 0 && (
        <Card className="mt-4 max-w-xl">
          <CardHeader>
            <CardTitle>График оплаты</CardTitle>
          </CardHeader>
          <CardBody>
            <PaymentScheduleList stages={order.paymentStages} />
          </CardBody>
        </Card>
      )}

      {/* Оплаты неизвестны (копия до появления поля) - блока нет: «оплат нет» было бы неправдой. */}
      {order.arePaymentsKnown && (
        <Card className="mt-4 max-w-xl">
          <CardHeader>
            <CardTitle>Оплаты</CardTitle>
          </CardHeader>
          <CardBody className="text-[13px] text-ink-800">
            {order.payments.length > 0 ? (
              <PaymentList payments={order.payments} />
            ) : (
              <span className="text-ink-500">
                Оплат со ссылкой на заказ в 1С нет
              </span>
            )}
          </CardBody>
        </Card>
      )}
    </>
  );
};
